//askSmart-ui/src/pages/AdminSMRTDocsViewWrapper.tsx
import React, { useState, useEffect } from "react";
import { useNavigate, useParams, useLocation } from "react-router-dom";
import AdminSMRTDocsView from "./AdminSMRTDocsView";
import { resolveServiceStepsAt } from "./smrtdocsServices";
import CollaboratorsPanel from "../components/CollaboratorsPanel";
import InviteCollaboratorModal from "../components/InviteCollaboratorModal";
import "./ManageProfiles.css";

export default function AdminSMRTDocsViewWrapper() {
  const { userId: rawUserId } = useParams();
  const userId = rawUserId ? decodeURIComponent(rawUserId) : null;
  const location = useLocation();
  const navigate = useNavigate();

  const [services, setServices] = useState<any[]>([]);
  const [assignments, setAssignments] = useState<any>({});
  // Gates the default-tab effect: until assignments have actually loaded we
  // can't tell "no services" from "not fetched yet", and computing a
  // collaborator fallback too early would pin it on first paint.
  const [loaded, setLoaded] = useState(false);

  useEffect(() => {
    Promise.all([
      fetch(`${process.env.REACT_APP_BACKEND_URL}/smrtdocs/services`, { credentials: "include" }).then(r => r.json()),
      fetch(`${process.env.REACT_APP_BACKEND_URL}/smrtdocs/assignments`, { credentials: "include" }).then(r => r.json()),
    ])
      .then(([svcData, asgnData]) => {
        setServices(svcData.services || []);
        setAssignments(asgnData.assignments || {});
        setLoaded(true);
      })
      .catch(err => console.error("Failed to load SMRT Docs data:", err));
  }, []);
  // Active tab: a service index (one per assigned service) or "collaborator".
  const [activeTab, setActiveTab] = useState<number | "collaborator" | null>(null);
  const [inviteOpen, setInviteOpen] = useState(false);
  const [collabRefresh, setCollabRefresh] = useState(0);
  const assigned = userId ? assignments[userId] : null;
  // Dangling indices — a service that no longer exists at that position —
  // are dropped rather than drawn as an empty "Service N" tab. See the same
  // filter on the user's own view in SMRTDocs.tsx.
  const assignedIndices: number[] = (
    assigned?.mode === "single" ? [assigned.value] :
    assigned?.mode === "multi" ? (assigned.values || []) : []
  ).filter((idx) => services.length === 0 || Boolean(services[idx]));

  const tabStorageKey = userId ? `smrtdocs-admin-tab-${userId}` : null;

  useEffect(() => {
    if (!loaded || !userId) return;
    const current = assignments[userId];
    let indices: number[] = [];
    if (current?.mode === "single") indices = [current.value];
    else if (current?.mode === "multi") indices = current.values || [];

    setActiveTab((prev) => {
      // Keep a still-valid explicit selection.
      if (prev === "collaborator") return prev;
      if (typeof prev === "number" && indices.includes(prev)) return prev;
      // Restore the last tab this admin viewed for this user, if still valid.
      const saved = tabStorageKey ? localStorage.getItem(tabStorageKey) : null;
      if (saved === "collaborator") return "collaborator";
      if (saved !== null) {
        const n = Number(saved);
        if (!Number.isNaN(n) && indices.includes(n)) return n;
      }
      // Otherwise default to the first assigned service.
      return indices.length > 0 ? indices[0] : "collaborator";
    });
  }, [assignments, userId, loaded, tabStorageKey]);

  // Persist the active tab so it's restored on the next visit to this user.
  useEffect(() => {
    if (activeTab === null || !tabStorageKey) return;
    localStorage.setItem(tabStorageKey, String(activeTab));
  }, [activeTab, tabStorageKey]);

  const [userName, setUserName] = useState<string>((location.state as any)?.name || userId || "");
  const [userEmail, setUserEmail] = useState<string>((location.state as any)?.email || "");

  useEffect(() => {
    if ((location.state as any)?.name) return;
    if (!userId) return;
    fetch(`${process.env.REACT_APP_BACKEND_URL}/approved_users`, { credentials: "include" })
      .then(r => r.json())
      .then(data => {
        const users = data.users || data || [];
        const match = users.find((u: any) => u.user_id === userId || u.id === userId);
        if (match) {
          setUserName(match.name || match.email || userId);
          setUserEmail(match.email || "");
        }
      })
      .catch(err => console.error("Failed to load user info:", err));
  }, [userId, location.state]);

  return (
    <div className="manage-profiles-page">
      <div
        style={{
          display: "flex",
          justifyContent: "space-between",
          marginBottom: "16px",
        }}
      >
        <h1>
          SMRT Docs (Admin View) — {userName}
          {typeof activeTab === "number" && services[activeTab] && (
            <> — {services[activeTab]?.name}</>
          )}
        </h1>
        <button
          className="btn-cancel"
          style={{ padding: "6px 12px", height: "32px" }}
          onClick={() => navigate(-1)}
        >
          Back
        </button>
      </div>

      {/* Tabs: one per assigned service, then Collaborator */}
      <div className="template-tabs">
        {assignedIndices.map((idx) => (
          <div
            key={idx}
            className={`template-tab ${activeTab === idx ? "active" : ""}`}
            onClick={() => setActiveTab(idx)}
          >
            <span>{services[idx]?.name || `Service ${idx + 1}`}</span>
          </div>
        ))}
        <div
          className={`template-tab ${activeTab === "collaborator" ? "active" : ""}`}
          onClick={() => setActiveTab("collaborator")}
        >
          <span>Collaborator</span>
        </div>
        {activeTab === "collaborator" && (
          <div
            className="template-tab template-tab-plus"
            title="Invite Collaborator"
            onClick={() => setInviteOpen(true)}
          >
            +
          </div>
        )}
      </div>

      {typeof activeTab === "number" && (
        <div className="smrtdocs-content template-card smrtdocs-admin">
          <AdminSMRTDocsView
            template={{ steps: resolveServiceStepsAt(services, activeTab) }}
            services={services}
            assigned={assigned}
            viewServiceIndex={activeTab}
            setViewServiceIndex={(i: number) => setActiveTab(i)}
            userId={userId}
            userEmail={userEmail}
            userName={userName}
          />
        </div>
      )}

      {activeTab === "collaborator" && (
        <div className="smrtdocs-content template-card smrtdocs-admin">
          <CollaboratorsPanel ownerUserId={userId || ""} refreshKey={collabRefresh} />
        </div>
      )}

      <InviteCollaboratorModal
        isOpen={inviteOpen}
        onClose={() => setInviteOpen(false)}
        ownerUserId={userId || undefined}
        onDone={() => setCollabRefresh((n) => n + 1)}
      />
    </div>
  );
}
